import {
  Component,
  computed, effect,
  ElementRef,
  EventEmitter, input,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
} from "@angular/core";
import {CommonModule} from "@angular/common";
import {NgbAccordionModule} from "@ng-bootstrap/ng-bootstrap";
import {ApiService, Tables} from "../../../services/api.service";
import {SelectionService} from "../../../services/selection.service";
import {
  AbstractControl,
  FormControl,
  FormControlName,
  FormsModule,
  ReactiveFormsModule, ValidationErrors,
  Validators
} from "@angular/forms";
import * as CryptoJS from "crypto-js";
import {MatChipsModule} from "@angular/material/chips";
import {MatExpansionModule} from "@angular/material/expansion";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {EditorComponent} from "../../../shared/components/editor/editor.component";
import {filter, map, Observable, startWith, Subject, Subscription, switchMap, tap} from "rxjs";
import {SafeHtmlPipe} from "src/app/core/pipes/safe-html.pipe";
import {UserService} from "src/app/services/user.service";
import {ReportService} from "src/app/features/error-report/data-access/report.service";
import {MalfunctionStatus} from "src/app/shared/interfaces/report";
import {KnowledgeBaseService} from "../data-access/knowledge-base.service";
import {SolutionService} from "../data-access/solution.service";
import {MediaItemComponent} from "./media-item/media-item.component";
import {ModalService} from "src/app/shared/data-access/modal.service";
import {HeaderService} from "src/app/services/header.service";
import {EditTreeItemService} from "../data-access/edit-tree-item.service";
import {Userrole} from "src/app/shared/interfaces/user";
import {SolutionMedia} from "src/app/shared/interfaces/media";
import {EscalationStatus, EscalationUpdate, getEscalationStatus} from "../../escalation/escalation.model";
import {EscalationService} from "../../escalation/data-access/escalation.service";
import {ViewModel} from "../../report/report.component";
import {transformReport} from "src/app/shared/interfaces/report";
import {getTimeZoneString} from "../../../shared/helpers/dateTime/getTimeZoneString";
import {calcTimeDifference} from "../../../shared/helpers/dateTime/calcTimeDifference";
import {EscalationDetailsComponent} from "../../escalation/components/escalation-details/escalation-details.component";
import {NavigationService} from "../../../shared/data-access/navigation.service";
import {Solution} from "../../../shared/interfaces/solution";
import {ReportDetailsComponent} from "./report-details/report-details.component";
import {MatIconModule} from "@angular/material/icon";
import {MatTooltip} from "@angular/material/tooltip";
import {Workplace} from "../../../shared/interfaces/workplace";
import {Malfunction} from "../../../shared/interfaces/malfunction";
import {MatMenuModule} from "@angular/material/menu";
import {MatDivider} from "@angular/material/divider";
import {SelectSolutionButtonComponent} from "./select-solution-button/select-solution-button.component";
import {AiEditorComponent} from "./ai-editor/ai-editor.component";

@Component({
  selector: "app-kd-solution",
  standalone: true,
  imports: [
    CommonModule,
    NgbAccordionModule,
    FormsModule,
    ReactiveFormsModule,
    MatChipsModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    EditorComponent,
    SafeHtmlPipe,
    MediaItemComponent,
    EscalationDetailsComponent,
    ReportDetailsComponent,
    MatTooltip,
    MatMenuModule,
    SelectSolutionButtonComponent,
    AiEditorComponent,
  ],
  templateUrl: "./kd-solution.component.html",
  styleUrls: ["./kd-solution.component.scss"],
})
export class KdSolutionComponent implements OnInit, OnChanges, OnDestroy {
  vm = input<ViewModel>();
  @Output() reportSolvedEvent = new EventEmitter<void>();
  @Output() reportEscalationEvent = new EventEmitter<EscalationUpdate>();
  @Output() reportDeletedEvent = new EventEmitter<string>();
  @Output() reportSolutionSelectedEvent = new EventEmitter<Solution>();
  @Output() escalationProcessingStatusUpdated = new EventEmitter<string>();
  @ViewChild("mediaInput") mediaInput!: ElementRef<HTMLInputElement>;
  ALLOW_MITARBEITER_EDIT_INTERVAL = 60 * 60 * 1000 // 1h -> 60 min/h * 60 s/min * 1000 ms/s

  protected readonly MalfunctionStatus = MalfunctionStatus;
  protected readonly EscalationStatus = EscalationStatus;
  protected readonly getTimeZoneString = getTimeZoneString;
  protected readonly calcTimeDifference = calcTimeDifference;
  protected readonly getEscalationStatus = getEscalationStatus
  Userrole = Userrole;

  editorData$ = new Subject<string>();
  editorData: string = "";
  solutionPrevention$ = new Subject<string>();
  solutionPrevention: string = "";
  solutionAdditionalInformation$ = new Subject<string>();
  solutionAdditionalInformation: string = "";
  solutionBeingEdited$ = new Subject<any>();
  solutionBeingEdited: {
    id: string;
    name: string;
    description: string;
    additional_information: string;
    prevention: string;
  } = {
    id: "",
    name: "",
    description: "",
    additional_information: "",
    prevention: "",
  };
  vmSignal = signal<ViewModel | undefined>(undefined);
  hasSolution = computed(() => {
    const solution = this.vmSignal()?.report?.solution as Solution;
    if (this.knowledgeBaseService.activeModule() === 'reportDowntime') {
      return true;
    } else if (!this.vmSignal() || !solution) {
      return false
    } else {
      return !(
        !solution.name
        && solution.description.includes(this.solutionService.NO_SOLUTION_AVAILABLE));
    }
  })
  downtimeExcludingClosure = signal<string>("")
  protected readonly availableSolutions = signal<Solution[]>([]);
  protected solutionNameControl = new FormControl('', [Validators.required, this.noEmptyStringValidator]);
  private solutionNameValidator: Subscription;

  protected editComment = signal<boolean>(false);
  escalationProcessingStatus = new FormControl('')

  /*
  * if no user has accepted the escalation yet, it is visible to all members of the assigned role.
  * if the escalation has been accepted by a user, it can only be processed by this user
  * */
  isAssignedToEscalation(): boolean {
    if (this.vm()?.escalation?.assigned_to_user) {
      return this.vm()?.escalation?.assigned_to_user === this.userService.user()?.id
    } else {
      return this.vm()?.escalation?.assigned_to_role === this.userService.userrole()
    }
  }

  get showEditSection(): boolean {
    const isMitarbeiterRole = this.userService.userrole() === Userrole.Mitarbeiter;
    const mitarbeiterCanEdit = this.allowSolutionEditing();
    const notSolvedDowntime = !(this.knowledgeBaseService.activeModule() === 'downtimeDetails' && this.vm()?.report?.status === MalfunctionStatus.SOLVED);
    return (!isMitarbeiterRole || mitarbeiterCanEdit) && notSolvedDowntime;
  }

  get showSolutionNameInput(): boolean {
    /*return this.knowledgeBaseService.activeModule() === 'downtimeDetails' && !this.hasSolution() &&
      this.solutionService.editSolution()*/
    return this.solutionService.editSolution();
  }

  showWorkflowButtons(): boolean {
    return !(
      this.solutionService.editSolution()
      || this.knowledgeBaseService.activeModule() === 'knowledgeBase'
      ||
      (this.knowledgeBaseService.activeModule() === 'downtimeDetails' && this.vm()?.report.status === MalfunctionStatus.SOLVED)
      ||
      (this.vm()?.report.status === MalfunctionStatus.ESCALATED
        && (this.vm()?.escalation?.status === EscalationStatus.CREATED
          || this.vm()?.escalation?.status === EscalationStatus.ACCEPTED))
      || (this.vm()?.report.status === MalfunctionStatus.ESCALATED && this.vm()?.escalation?.status ===
        EscalationStatus.IN_PROGRESS && !this.isAssignedToEscalation())

    )
  }

  calculateDurationExcludingClosure(startDate: string, endDate: string): void {
    if (endDate === null) {
      return;
    } else {
      const roundTimestampToMinutes = function (dateTimeString: string) {
        let date = new Date(dateTimeString);
        date.setSeconds(0, 0);
        return new Date(date).getTime();
      };

      let startTime = roundTimestampToMinutes(startDate);
      let endTime = roundTimestampToMinutes(endDate);

      this.apiService.calculateDurationExcludingClosure(new Date(startTime), new Date(endTime))
        .pipe(
          map((diff) => {
            const hours = String(Math.floor(diff / 1000 / 3600)).padStart(2, "0");
            const minutes = String(Math.floor((diff / 1000 % 3600) / 60)).padStart(2, "0");
            if (hours === "00" && minutes === "00") {
              return `< 1 min`;
            } else {
              return `${hours}:${minutes}`;
            }
          })
        ).subscribe((result) => this.downtimeExcludingClosure.set(result));
    }
  }

  constructor(
    protected readonly apiService: ApiService,
    protected readonly userService: UserService,
    private headerService: HeaderService,
    protected readonly selectionService: SelectionService,
    protected knowledgeBaseService: KnowledgeBaseService,
    protected escalationService: EscalationService,
    private editTreeItemService: EditTreeItemService,
    protected solutionService: SolutionService,
    protected readonly reportService: ReportService,
    public modalService: ModalService,
    private navigationService: NavigationService
  ) {
    effect(() => {
      if (this.solutionService.solution().name) {
        this.solutionNameControl.setValue(this.solutionService.solution().name);
      }
    })
  }

  private updateValidators(): void {
    const control = this.solutionNameControl;
    control.clearValidators();

    if (!this.hasSolution()) {
      control.addValidators([Validators.required, this.noEmptyStringValidator]);
    }
    control.updateValueAndValidity({emitEvent: false});
  }

  ngOnInit(): void {
    if (this.solutionService.isSearchResult()) {
      this.headerService.setHeaderSubTitle(
        this.solutionService.searchPath()
      )
    } else {
      this.headerService.setHeaderSubTitle(
        `${this.knowledgeBaseService.expandedNodes().productionLine?.name} - ${
          this.knowledgeBaseService.expandedNodes().workplace?.name
        } - ${this.knowledgeBaseService.expandedNodes().malfunction?.name}`
      );
    }
    this.solutionNameValidator = this.solutionNameControl.valueChanges.pipe(
      tap(() => {
        this.solutionNameControl.markAsDirty();
        this.updateValidators();
      }),
      startWith(this.solutionNameControl.value)
    ).subscribe();
    this.editorData$.subscribe((data) => (this.editorData = data));
    this.solutionPrevention$.subscribe(
      (data) => (this.solutionPrevention = data)
    );
    this.solutionAdditionalInformation$.subscribe(
      (data) => (this.solutionAdditionalInformation = data)
    );
    this.solutionBeingEdited$.subscribe(
      (solution: any) => (this.solutionBeingEdited = solution)
    );
  }

  ngOnChanges() {
    this.vmSignal.set(this.vm());
    // update view after report data was fetched from the database
    if (Object.keys(this.vm()?.report!).length > 0 && (this.vm()?.report?.status === MalfunctionStatus.ESCALATED || this.knowledgeBaseService.activeModule() === 'downtimeDetails')) {
      this.reportService.currentReport$.next(transformReport(this.vm()?.report!))
    }
    if (this.vmSignal()?.report.created_at && this.vmSignal()?.report.solved_on) {
      this.calculateDurationExcludingClosure(this.vmSignal()?.report.created_at!, this.vmSignal()?.report.solved_on!)
    }
    if (this.vmSignal()?.report?.malfunction) {
      const malfunctionId = (this.vmSignal()?.report.malfunction as Malfunction).id;
      this.apiService.getSolutionsForMalfunction(malfunctionId).pipe(
        map(solutions => solutions.filter(s => !!s.name))
      ).subscribe((solutions) => this.availableSolutions.set(solutions))
    }
  }

  ngOnDestroy() {
    this.solutionService.editSolution.set(false);
    this.solutionService.isSearchResult.set(false);
    this.solutionNameControl.reset();
    this.solutionNameValidator.unsubscribe();
  }

  private noEmptyStringValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (typeof value === 'string' && value.trim().length === 0) {
      return {noEmptyString: true};
    }
    return null;
  }

  editFilename(item: SolutionMedia) {
    if (item) {
      this.solutionService.mediaItemBeingEdited$.next(item);
    }
  }

  deleteMediaItem(item: SolutionMedia) {
    if (item) {
      this.solutionService.deleteMediaItem$.next(item);
    }
  }

  editSolution() {
    this.solutionNameControl.setValue(this.solutionService.solution().name);
    this.solutionBeingEdited$.next(this.solutionService.solution());
    this.solutionService.editSolution.set(true);
  }


  updateSolution() {
    if (this.solutionNameControl.valid && this.knowledgeBaseService.activeModule() === 'knowledgeBase' || (!this.hasSolution() && this.solutionNameControl.valid) || this.hasSolution()) {
      console.log(this.solutionBeingEdited)
      let newName = this.solutionNameControl.value!;
      let newDescription = this.editorData
        ? this.editorData
        : this.solutionBeingEdited.description;
      let newPrevention = this.solutionPrevention
        ? this.solutionPrevention
        : this.solutionBeingEdited.prevention;
      let newAdditionalInformation = this.solutionAdditionalInformation
        ? this.solutionAdditionalInformation
        : this.solutionBeingEdited.additional_information;
      this.solutionService.updateSolution(
        newName,
        newDescription,
        newPrevention,
        newAdditionalInformation
      );
      this.apiService
        .updateItem<Pick<Solution, 'id' | 'name' | 'description' | 'prevention' | 'additional_information'>, Solution>(Tables.Solution, {
          id: this.solutionBeingEdited.id
            ? this.solutionBeingEdited.id
            : this.solutionService.solution().id,
          name: newName,
          description: newDescription,
          prevention: newPrevention,
          additional_information: newAdditionalInformation,
        })
        .then((data) => {
          if (this.vmSignal()) {
            const currentVm = this.vmSignal()!;
            const updatedVm = {
              ...currentVm,
              report: {
                ...currentVm.report,
                solution: data
              }
            }
            this.vmSignal.set(updatedVm)
          }
          this.solutionService.editSolution.set(false)
        });
    } else {
      window.alert('Bitte geben Sie einen Namen für die Lösung an.')
      this.solutionNameControl.markAsDirty()
    }
  }

  /**
   * triggers adding a new solution.
   * The new solution will be added to the knowledgeBase and stored as the solution for the current report.
   */
  addNewSolution() {
    /*
    * if a report id exists update reportBeingUpdated subject
    * create a new item in knowledge base tree
    * open the detail page of the newly created item
    * */
    if (this.knowledgeBaseService.activeModule() === 'downtimeDetails') {
      this.reportService.reportBeingUpdated$.next(this.reportService.currentReport()!.id!);
    }
    this.editTreeItemService.action.set("addSolution");
    this.knowledgeBaseService.treeInputLevel.set(2);
    this.editTreeItemService.modalHeading$.next("Neue Lösung hinzufügen");
    this.modalService.openModal("itemFormModal");
  }

  selectSolution(solution: Solution) {
    // Check if this is a new report without a real solution
    const currentSolution = this.vmSignal()?.report?.solution as Solution;
    const isTemporarySolution = !this.hasSolution() && currentSolution?.id;

    if (isTemporarySolution) {
      // Mark the temporary solution as deleted
      this.apiService.updateItem(Tables.Solution, {
        id: currentSolution.id,
        is_deleted: true
      }).then(() => {
        // Emit the new solution selection
        this.reportSolutionSelectedEvent.emit(solution);
      }).catch((error) => {
        console.error('Error marking temporary solution as deleted:', error);
        // Still proceed with solution selection even if deletion fails
        this.reportSolutionSelectedEvent.emit(solution);
      });
    } else {
      // Report already has a real solution, just emit the new selection
      this.reportSolutionSelectedEvent.emit(solution);
    }
  }

  updateEscalation(escalationUpdate: EscalationUpdate) {
    this.reportEscalationEvent.emit(escalationUpdate)
    this.escalationService.updateEscalation(escalationUpdate)
  }

  protected updateEscalationProcessingStatus(event: { escalationId: string; message: string }) {
    this.escalationService.updateEscalationProcessingStatus(event.escalationId, event.message).pipe(
      tap(updatedEscalation => {
        this.vmSignal.update(vm => ({
          ...vm!,
          escalation: updatedEscalation
        }))
      })
    ).subscribe();
  }

  solveReport() {
    if (this.knowledgeBaseService.activeModule() === "reportDowntime") {
      this.reportService.solveActiveReport();
    } else {
      this.reportSolvedEvent.emit();
    }
  }

  onFileSelected(event: any) {
    for (let file of event.target.files) {
      const renamedFile = new File(
        [file],
        CryptoJS.SHA256(file.name + Math.random() * 100000).toString(),
        {type: file.type}
      );
      this.apiService
        .uploadMediaFile(
          renamedFile,
          file.name,
          this.solutionService.solution().id
        )
        .pipe(switchMap(() => this.solutionService.refreshMediaFiles()))
        .subscribe();
    }
    this.mediaInput.nativeElement.value = "";
  }

  storeEditorData(event: string, property: Subject<string>) {
    property.next(event);
  }

  allowSolutionEditing() {
    const isCreator = this.solutionService.solution().created_by === this.userService.user()!.id
    const currentDate = new Date()
    const interval = currentDate.getTime() - (new Date(this.solutionService.solution().created_at)).getTime()
    return isCreator && interval < this.ALLOW_MITARBEITER_EDIT_INTERVAL
  }

  setNotificationMessage() {
    if (this.vmSignal()?.escalation?.processing_status_notification) {
      this.escalationProcessingStatus.setValue(this.vmSignal()!.escalation!.processing_status_notification);
    }
  }

  protected saveComment() {
    console.log("save comment")
    this.editComment.set(false);
    this.updateEscalationProcessingStatus({
      escalationId: this.vmSignal()!.escalation!.id,
      message: this.escalationProcessingStatus.value!
    })
  }

  back() {
    if (this.solutionService.isSearchResult()) {
      this.knowledgeBaseService.changeActivePage(3);
      this.solutionService.isSearchResult.set(false);
    } else if (this.knowledgeBaseService.activeModule() === "downtimeDetails") {
      this.knowledgeBaseService.resetState(["workplaceSelectionData"]);
      this.knowledgeBaseService.changeActivePage(0);
      this.navigationService.navigateBack();
    } else {
      this.knowledgeBaseService.goBack();
      this.knowledgeBaseService.changeActivePage(1);
    }
    this.solutionService.editSolution.set(false);
  }
}
